import { getMilliseconds, decayingInterval } from '@trello/time';
import { smallestPreviewBiggerThan } from '@trello/image-previews';
import React, { useEffect, useRef, useCallback } from 'react';
import { MinimalCanonicalCard } from './MinimalCanonicalCard';
import { usePopulatingMinimalCanonicalCardQuery } from './PopulatingMinimalCanonicalCardQuery.generated';
import type { PopulatingMinimalCanonicalCardQuery } from './PopulatingMinimalCanonicalCardQuery.generated';
import { trelloFetch } from '@trello/fetch';
import { sendErrorEvent } from '@trello/error-reporting';
import { clientVersion } from '@trello/config';
import { isFetchCancellationError } from '@trello/error-handling';

export interface BoardBackgroundType {
  backgroundBottomColor: string | null;
  backgroundBrightness: 'light' | 'dark' | 'unknown';
  backgroundImage?: string | null;
  backgroundTile: boolean;
  backgroundTopColor: string | null;
  backgroundImageScaled?: {
    width: number;
    height: number;
    url: string;
  }[];
}

interface Props {
  background?: BoardBackgroundType | null;
  boardName: string;
  boardUrl: string;
  cardName: string;
  cardUrl: string;
  className?: string;
  idBoard: string;
  idCard: string;
  isWide?: boolean;
  listName: string;
  onClickCardLink?: () => void;
  onClickBoardName?: () => void;
}

export const PopulatingMinimalCanonicalCard = ({
  idCard,
  background,
  boardName,
  boardUrl,
  cardName,
  cardUrl,
  isWide,
  listName,
  className,
  onClickCardLink,
  onClickBoardName,
}: Props) => {
  const ixLastUpdateForBoardRef = useRef<number | null>(null);
  const cancelInterval = useRef<() => void>();
  // need the refetch for polling
  // eslint-disable-next-line @trello/no-apollo-refetch
  const { data, refetch } = usePopulatingMinimalCanonicalCardQuery({
    variables: { idCard },
    waitOn: ['None'],
  });
  const card = data?.card;

  const refetchBasedOnIxLastUpdateForBoard = useCallback(async () => {
    try {
      if (!card) {
        return;
      }

      const response = await trelloFetch(
        `/1/board/${card.idBoard}/deltas?ixLastUpdate=-1&tags=clientActions,updates`,
        undefined,
        {
          clientVersion,
          networkRequestEventAttributes: {
            source: 'trelloFetch',
            operationName: 'CardNotificationLastIxUpdate',
          },
        },
      );
      if (!response.ok) {
        return;
      }

      const data = await response.json();
      if (data?.modelIxUpdate?.[card.idBoard]) {
        if (
          // not the first time running this since opening notifications
          ixLastUpdateForBoardRef.current !== null &&
          // there is an update on the board. Can't know if that is for a card, but we can
          // check by doing a refetch and updating the cache
          data.modelIxUpdate[card.idBoard] !== ixLastUpdateForBoardRef.current
        ) {
          refetch();
        }
        ixLastUpdateForBoardRef.current = data.modelIxUpdate[card.idBoard];
      }
    } catch (err) {
      if (err instanceof Error && isFetchCancellationError(err)) {
        return;
      }

      // @ts-expect-error
      sendErrorEvent(err, {
        tags: {
          ownershipArea: 'trello-fe-platform',
          feature: 'Notifications',
        },
        extraData: {
          component: 'PopulatingMinimalCanonicalCard',
        },
      });
    }
  }, [card, refetch]);

  useEffect(() => {
    if (!card) {
      refetch();
    } else {
      refetchBasedOnIxLastUpdateForBoard();
    }

    cancelInterval.current = decayingInterval(
      refetchBasedOnIxLastUpdateForBoard,
      {
        resetAfterMouseMove: true,
        intervals: [
          {
            iterations: 2,
            timeout: getMilliseconds({ seconds: 10 }),
          },
        ],
      },
    );

    return () => {
      if (cancelInterval.current) {
        cancelInterval.current();
      }
    };
    // Should only run on mount and unmount
    // eslint-disable-next-line @trello/exhaustive-deps
  }, []);

  const getBoardBackgroundData = (background?: BoardBackgroundType | null) => {
    let boardBgColor;
    let boardBgBottomColor;
    let boardBgBrightness;
    let boardBgImage;

    if (background) {
      const preview = smallestPreviewBiggerThan(
        background && background.backgroundImageScaled,
        250,
        130,
      );

      boardBgColor = background.backgroundTopColor || undefined;
      boardBgBottomColor = background.backgroundBottomColor || undefined;
      boardBgBrightness = background.backgroundBrightness || undefined;

      boardBgImage = preview
        ? preview.url
        : background.backgroundImage || undefined;
    }

    return {
      boardBgColor,
      boardBgBottomColor,
      boardBgBrightness,
      boardBgImage,
    };
  };

  const extractMinimalProps = ({
    board,
    card,
    list,
  }: {
    board?: {
      prefs?: BoardBackgroundType | null;
      name: string;
      url: string;
    };
    card?: PopulatingMinimalCanonicalCardQuery['card'];
    list?: { name: string };
  }) => {
    const badges = card ? card.badges : null;

    return {
      ...getBoardBackgroundData((board && board.prefs) || background),
      boardName: (board && board.name) || boardName,
      boardUrl: (board && board.url) || boardUrl,
      cardDue: badges?.due || card?.due,
      cardStart: (badges && badges.start) || card?.start,
      cardDueComplete: (badges && badges.dueComplete) || card?.dueComplete,
      cardIsTemplate: card?.isTemplate,
      cardRole: card?.cardRole,
      cardName: (card && card.name) || cardName,
      cardUrl: (card && card.url) || cardUrl,
      isWide: isWide,
      listName: (card && list && list.name) || listName,
    };
  };

  if (!card) {
    return null;
  }

  return (
    <MinimalCanonicalCard
      {...extractMinimalProps({
        board: card?.board,
        card: data.card,
        list: card?.list,
      })}
      className={className}
      onClickCardLink={onClickCardLink}
      onClickBoardName={onClickBoardName}
    />
  );
};
